export const MEASURE_CATEGORY_IDS = [
  'sleep',
  'fitness',
  'heart',
  'women',
  'longevity',
  'powerplugs',
] as const;

export const MEASURE_FEATURE_IDS = ['1', '2', '3', '4'] as const;

export const MEASURE_DEFAULT_OPEN = '1';

export type MeasureCategoryId = (typeof MEASURE_CATEGORY_IDS)[number];
export type MeasureFeatureId = (typeof MEASURE_FEATURE_IDS)[number];

type Rect = readonly [x: number, y: number, w: number, h: number];

/** Where a video sits on the 640x620 stage, in %: `frame` clips to the phone, `video` is the full square file. */
export type VideoFit = {
  frame: Rect;
  video: Rect;
  /** The phone runs off the bottom of the card for `top`. */
  corners: 'top' | 'all';
};

export type MeasureMedia =
  | { kind: 'image'; src: string }
  | { kind: 'video'; src: string; poster: string; fit: VideoFit };

export type MeasureFeature = {
  id: string;
  title: string;
  titleMobile?: string;
  body: string;
  bodyMobile?: string;
  media?: MeasureMedia;
  link?: {
    label: string;
    href: string;
  };
};

const MEASURE_MEDIA_CACHE = '?v=2';

const withCache = (src: string) => `${src}${MEASURE_MEDIA_CACHE}`;

const image = (src: string): MeasureMedia => ({
  kind: 'image',
  src: withCache(src),
});

const video = (src: string, poster: string, fit: VideoFit): MeasureMedia => ({
  kind: 'video',
  src: withCache(src),
  poster: withCache(poster),
  fit,
});

const WORKOUT_FIT: VideoFit = {
  frame: [20.35, 12.4, 59.3, 87.6],
  video: [1.59, 0, 96.8, 100.1],
  corners: 'top',
};

const CYCLE_FLAGS_FIT: VideoFit = {
  frame: [30.08, 7.42, 39.84, 85.16],
  video: [8.114, 6.75, 83.741, 86.442],
  corners: 'all',
};

export const MEASURE_LINKS: Record<
  MeasureCategoryId,
  Record<MeasureFeatureId, string>
> = {
  sleep: {
    '1': '/science/explainers/sleep-index/',
    '2': '/science/explainers/sleep-stages/',
    '3': '/science/explainers/skin-temperature/',
    '4': '/science/explainers/caffeine-window/',
  },
  fitness: {
    '1': '/science/explainers/movement-index/',
    '2': '/science/explainers/active-vs-inactive-hours/',
    '3': '/science/explainers/heart-rate-zones/',
    '4': '/science/explainers/total-calories-tdee/',
  },
  heart: {
    '1': '/science/explainers/recovery-score/',
    '2': '/science/explainers/heart-rate-variability/',
    '3': '/science/explainers/resting-heart-rate/',
    '4': '/science/explainers/resting-heart-rate-when-sick/',
  },
  women: {
    '1': '/science/explainers/women-s-health-cycle-insights/',
    '2': '/science/explainers/ovulation-confirmation-states/',
    '3': '/science/explainers/cycle-flags-anovulation/',
    '4': '/science/explainers/pregnancy-insights/',
  },
  longevity: {
    '1': '/science/explainers/cardio-age/',
    '2': '/science/explainers/cardio-adaptability/',
    '3': '/science/explainers/circadian-alignment/',
    '4': '/science/explainers/sleep-consistency/',
  },
  powerplugs: {
    '1': '/powerplugs/',
    '2': '/powerplugs/',
    '3': '/powerplugs/',
    '4': '/powerplugs/',
  },
};

export const MEASURE_MEDIA: Record<
  MeasureCategoryId,
  Partial<Record<MeasureFeatureId, MeasureMedia>>
> = {
  sleep: {
    '1': image('web_v2/how-it-works/measures-static/sleep/sleep-index-latest.png'),
    '2': image('web_v2/how-it-works/measures-static/sleep/sleep-stages.png'),
    '3': image('web_v2/how-it-works/measures-static/sleep/skin-temperature.png'),
    '4': image('web_v2/how-it-works/measures-static/sleep/caffeine-window.png'),
  },
  fitness: {
    '1': image('web_v2/how-it-works/measures-static/fitness/movement-index-latest.png'),
    '2': image(
      'web_v2/how-it-works/measures-static/fitness/steps-and-active-hours.png',
    ),
    '3': video(
      'web_v2/how-it-works/measures/fitness/workout-heart-rate-and-zones-latest.mp4',
      'web_v2/how-it-works/measures-static/fitness/workout-heart-rate-and-zones-poster-v3.png',
      WORKOUT_FIT,
    ),
    '4': image(
      'web_v2/how-it-works/measures-static/fitness/calories-burned-latest.png',
    ),
  },
  heart: {
    '1': image('web_v2/how-it-works/measures-static/heart/recovery-score.png'),
    '2': image('web_v2/how-it-works/measures-static/heart/heart-rate-variability.png'),
    '3': image('web_v2/how-it-works/measures-static/heart/resting-heart-rate.png'),
    '4': image(
      'web_v2/how-it-works/measures-static/heart/early-signs-of-strain-latest.png',
    ),
  },
  women: {
    '1': image('web_v2/how-it-works/measures-static/women/cycle-tracking.png'),
    '2': image('web_v2/how-it-works/measures-static/women/ovulation-confirmation.png'),
    '3': video(
      'web_v2/how-it-works/measures/women/cycle-flags-v3.mp4',
      'web_v2/how-it-works/measures-static/women/cycle-flags-poster-v3.png',
      CYCLE_FLAGS_FIT,
    ),
    '4': image('web_v2/how-it-works/measures-static/women/pregnancy-powerplug.png'),
  },
  longevity: {
    '1': image('web_v2/how-it-works/measures-static/longevity/cardio-age.png'),
    '2': image(
      'web_v2/how-it-works/measures-static/longevity/cardio-adaptability.png',
    ),
    '3': image(
      'web_v2/how-it-works/measures-static/longevity/circadian-alignment.png',
    ),
    '4': image('web_v2/how-it-works/measures-static/longevity/sleep-consistency.png'),
  },
  powerplugs: {
    '1': image('web_v2/how-it-works/measures-static/powerplugs/afib-detection.png'),
    '2': image('web_v2/how-it-works/measures-static/powerplugs/respiratory-health.png'),
    '3': image('web_v2/how-it-works/measures-static/powerplugs/smart-alarm.png'),
    '4': image('web_v2/how-it-works/measures-static/powerplugs/vitamin-d.png'),
  },
};
